import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AuthContext } from '../../context/AuthContext';
import { getLocalCache, setLocalCache } from '../../utils/localCache';
import { slugify } from '../../utils/slugify';
import { supabase } from '../../utils/supabase';

const CACHE_KEY = 'home_leaderboard_widget_v1';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10m

function safeLen(arr) {
  return Array.isArray(arr) ? arr.length : 0;
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
      }`}
    >
      {children}
    </button>
  );
}

export default function HomeLeaderboardWidget() {
  const { username, isAuthenticated, glPoints } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('most_played');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostPlayed, setMostPlayed] = useState([]);
  const [newestMembers, setNewestMembers] = useState([]);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const cached = getLocalCache(CACHE_KEY, CACHE_TTL_MS);
    if (cached?.mostPlayed && cached?.newestMembers) {
      setMostPlayed(cached.mostPlayed);
      setNewestMembers(cached.newestMembers);
      setIsLoading(false);
    }

    let cancelled = false;
    const load = async () => {
      setError(null);
      setIsLoading(true);
      try {
        // 1 query: enough columns for both tabs + lightweight personalization.
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profile')
          .select('username, profile_image_url, played_list, created_at, gl_points, greenfee_value')
          .limit(400);
        if (profilesError) throw profilesError;

        const rows = (profiles || []).filter((p) => p?.username);

        const mostPlayedTop = [...rows]
          .map((p) => ({
            username: p.username,
            profile_image_url: p.profile_image_url,
            played_count: safeLen(p.played_list),
            greenfee_value: p.greenfee_value ?? null,
          }))
          .sort((a, b) => b.played_count - a.played_count)
          .slice(0, 10)
          .map((p, idx) => ({ ...p, rank: idx + 1 }));

        const newestTop = [...rows]
          .filter((p) => p.created_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 10)
          .map((p, idx) => ({
            username: p.username,
            profile_image_url: p.profile_image_url,
            created_at: p.created_at,
            rank: idx + 1,
          }));

        const cachedPayload = {
          mostPlayed: mostPlayedTop,
          newestMembers: newestTop,
        };

        if (!cancelled) {
          setMostPlayed(mostPlayedTop);
          setNewestMembers(newestTop);
          setLocalCache(CACHE_KEY, cachedPayload);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load leaderboard');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !username) {
      setMe(null);
      return;
    }
    // Keep personalization cheap: just use AuthContext glPoints and a tiny profile fetch
    // to get played_count (no need to download the big list).
    let cancelled = false;
    const loadMe = async () => {
      try {
        const { data, error: meError } = await supabase
          .from('user_profile')
          .select('username, profile_image_url, played_list, created_at, gl_points, greenfee_value')
          .eq('username', username)
          .single();
        if (meError) throw meError;
        if (cancelled) return;
        setMe({
          username: data?.username,
          profile_image_url: data?.profile_image_url,
          played_count: safeLen(data?.played_list),
          created_at: data?.created_at,
          gl_points: data?.gl_points ?? glPoints,
          greenfee_value: data?.greenfee_value ?? null,
        });
      } catch {
        // Don’t block homepage on personalization failures.
        if (!cancelled) setMe(null);
      }
    };
    loadMe();
    return () => {
      cancelled = true;
    };
  }, [glPoints, isAuthenticated, username]);

  const rows = useMemo(() => {
    return activeTab === 'newest' ? newestMembers : mostPlayed;
  }, [activeTab, mostPlayed, newestMembers]);

  return (
    <section className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 overflow-hidden h-full min-h-[520px]">
      <div className="p-6 h-full flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
              Connect leaderboard
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              A snapshot of what the community is doing right now.
            </p>
            <p className="text-xxs text-slate-500 mt-1">
              GFV = Green Fee Value
            </p>
          </div>
          <Link
            to="/connect"
            className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
          >
            View full Connect →
          </Link>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <TabButton
            active={activeTab === 'most_played'}
            onClick={() => setActiveTab('most_played')}
          >
            Most played
          </TabButton>
          <TabButton active={activeTab === 'newest'} onClick={() => setActiveTab('newest')}>
            Newest members
          </TabButton>
        </div>

        {me?.username && (
          <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {me.profile_image_url ? (
                  <img
                    src={me.profile_image_url}
                    alt=""
                    className="w-9 h-9 rounded-full bg-slate-200"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-slate-200" />
                )}
                <div className="min-w-0">
                  <div className="text-xs text-slate-600">You</div>
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    <Link to={`/profile/${slugify(me.username)}`} className="hover:underline">
                      {me.username}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-600">Played</div>
                <div className="text-sm font-mono font-semibold text-slate-900">
                  {Number(me.played_count || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex-1 overflow-auto pr-1">
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : isLoading && rows.length === 0 ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : (
            <ol className="divide-y divide-slate-100">
              {rows.map((r) => (
                <li key={r.username} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 text-xs font-mono text-slate-500">#{r.rank}</div>
                    {r.profile_image_url ? (
                      <img
                        src={r.profile_image_url}
                        alt=""
                        className="w-8 h-8 rounded-full bg-slate-200"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200" />
                    )}
                    <Link
                      to={`/profile/${slugify(r.username)}`}
                      className="text-sm font-semibold text-slate-900 truncate hover:underline"
                    >
                      {r.username}
                    </Link>
                  </div>
                  <div className="text-right">
                    {activeTab === 'newest' ? (
                      <div className="text-xs text-slate-600">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="text-xs font-mono font-semibold text-slate-900">
                          {Number(r.played_count || 0).toLocaleString()}
                        </div>
                        {r.greenfee_value != null && (
                          <div className="text-xxs font-mono text-slate-600">
                            GFV £{Number(r.greenfee_value).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-xxs text-slate-500">
                      {activeTab === 'newest' ? 'Joined' : 'Played'}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}

